import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, ElementRef, inject, Input, OnInit, output, signal, untracked, viewChild, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CurrencyPipe, DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { RouterLink } from '@angular/router';
import { debounceTime, interval, startWith, Subject, Subscription, take } from 'rxjs';
import { FormField, form } from '@angular/forms/signals';
import { InfiniteScrollDirective } from 'ngx-infinite-scroll';
import { ManageBriefTabsService } from '../../service/manage-brief-tabs/manage-brief-tabs.service';
import { ImageService } from '../../../../services/image/image.service';
import { BriefService } from '../../../../services/brief/brief.service';
import { ApplicationStatusEnum, BriefApplicationResponseDto } from '../../../../common/general-types/briefs.dto';
import { FilterItemDto } from '../../../../common/general-types/filters.dto';
import { HasRoleDirectiveDirective } from '../../../../common/directives/has-role/has-role-directive.directive';
import { ShortNumberPipe } from '../../../../common/pipes/short-number/short-number.pipe';
import { listCreatorPlatformStats } from '../../../../common/format/creator-platform-stats';
import { personInitials } from '../../../../common/format/person-initials';
import { MagicSearchService } from '../../../creators/service/magic-search/magic-search.service';
import { BriefApplicationService } from '../../../../services/brief-application/brief-application.service';
import { SocialScreeningService } from '../../../../services/social-screening/social-screening.service';
import { CreateScreeningService } from '../../../screening-brief-create/services/create-screening/create-screening.service';
import { DeclineBriefApplicationDto } from '../../../../common/general-types/brief-application.dto';
import { ActionTypesEnum } from '../../../../common/app-config/filter-models/misc';
import { ToastNotificationService } from '../../../../atoms/toast-notification/toast-notification.service';
import { AlertService } from '../../../../atoms/alert/alert.service';
import { BroadcastMessage } from '../../../../atoms/broadcast-message/broadcast-message';
import { FirebaseService } from '../../../../common/firebase/firebase.service';
import { AuthService } from '../../../../services/auth/auth.service';
import { isShoutAgencyAdmin } from '../../../../common/support-admin-roles.util';
import { canUnhireApplication } from '../../../admin-options/settings/molecules/brief-tracker/brief-tracker.helpers';
import { Router } from '@angular/router';
import { applyVat, crossPostNetTotal } from './hire-cost.util';
import { evaluateApplicant } from './screening-qa.util';
import { AssignmentFilters } from './molecules/assignment-filters/assignment-filters';
import { assignmentSortOptions, isSameAssignmentSort } from './molecules/assignment-sort/assignment-sort.helpers';
import { CentralManagementService } from '../../../../services/central-management/central-management.service';
import { CrossPlatformTile } from './atoms/cross-platform-tile/cross-platform-tile';
import { isAppliedAutoDeclineExpired, isAutoDeclineEnabled } from './molecules/application-stage-progress/application-stage-progress.helpers';
import { PricingModelV2Service } from '../../../../services/pricing-model-v2/pricing-model-v2.service';
import {
  briefCrossChannels,
  crossPlatformCells,
  isApplicant,
  isCrossPlatformBrief,
  postStatusFor,
  primaryPostState,
  putForwardSummary,
  readChannels,
  togglableChannels,
  type CrossPlatformCell,
  type PrimaryPostState,
} from './cross-platform-state.util';
import { SubmittedPostStatusEnum } from '../../../../common/general-types/submitted-posts.dto';
import { getSocialChannelLabel } from '../../../insights/tabs/creator/molecules/application-drawer/molecules/application-actions/application-actions.helpers';
import { excludeHiddenAssignmentApplicants } from '../../service/manage-brief-tabs/applicant-filters.helpers';
import {
  assignmentStageIndex,
  assignmentStageLabels,
  briefKindFor,
  isLate,
  needsYou,
  nextStep,
  socialChannelName,
  stageElapsed,
  videoCounts,
  type NextStep,
  type VideoCounts,
} from './assignment-view.helpers';
import { MbaIcon } from './atoms/mba-icon/mba-icon';
import { VideoStatusPill } from './atoms/video-status-pill/video-status-pill';
import { StageSegments } from './molecules/stage-segments/stage-segments';
import { ApplicationCountdown } from './molecules/application-countdown/application-countdown';
import { NextStepCell } from './molecules/next-step-cell/next-step-cell';
import { StageTiles, type StageFilter, type TileSummary } from './molecules/stage-tiles/stage-tiles';
import { CreatorSidePanel } from './molecules/creator-side-panel/creator-side-panel';
import { ReasonModal } from './molecules/reason-modal/reason-modal';
import { ui } from './assignment-ui';

const ROW_INTERACTIVE_SELECTOR =
  'button, a, input, textarea, select, [contenteditable="true"], [data-row-click-ignore], [role="link"]';

function isRowInteractiveTarget(target: EventTarget | null): boolean {
  return target instanceof Element && !!target.closest(ROW_INTERACTIVE_SELECTOR);
}

/** Tile stage → the status filter the list already understands. */
const STATUS_FOR_STAGE: ApplicationStatusEnum[] = [
  ApplicationStatusEnum.Invited,
  ApplicationStatusEnum.Applied,
  ApplicationStatusEnum.Hired,
  ApplicationStatusEnum.Started,
  ApplicationStatusEnum.ReadyToPost,
  ApplicationStatusEnum.Completed,
];

/** Statuses a creator can be promoted from (matches the old row button). */
const PROMOTABLE_STATUSES = new Set<ApplicationStatusEnum>([
  ApplicationStatusEnum.Hired,
  ApplicationStatusEnum.Started,
  ApplicationStatusEnum.ReadyToPost,
  ApplicationStatusEnum.Posted,
  ApplicationStatusEnum.Completed,
  ApplicationStatusEnum.Paid,
  ApplicationStatusEnum.BonusPaid,
]);

export type RatingFilter = 'all' | 'reviewed' | 'new';

/** Everything one table row / card / panel needs, worked out once per render. */
export interface AssignmentRow {
  applicant: BriefApplicationResponseDto;
  stage: number;
  stageLabel: string;
  elapsed: string;
  late: boolean;
  needsYou: boolean;
  expired: boolean;
  step: NextStep;
  counts: VideoCounts;
  name: string;
  initial: string;
  photo: string | null;
  verdict: ReturnType<typeof evaluateApplicant> | null;
  avgViews: number | null;
  engagement: number | null;
}

interface ModalState {
  kind: 'decline' | 'unhire';
  applicant: BriefApplicationResponseDto;
}

@Component({
  selector: 'mbt-assignment',
  imports: [
    RouterLink, CurrencyPipe, DecimalPipe, NgTemplateOutlet, HasRoleDirectiveDirective, ShortNumberPipe, BroadcastMessage, FormField,
    InfiniteScrollDirective, AssignmentFilters, CrossPlatformTile, MbaIcon, VideoStatusPill, StageSegments,
    ApplicationCountdown, NextStepCell, StageTiles, CreatorSidePanel, ReasonModal,
  ],
  templateUrl: './assignment.component.html',
  styleUrl: './assignment.component.css',
  // Shared with the child molecules; every rule is scoped under `.mba`.
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex h-full max-h-full min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden',
    '(document:keydown)': 'onDocumentKeydown($event)',
  },
})
export class AssignmentComponent implements OnInit {

  @Input() selectedSubTab: number | undefined;

  protected readonly ApplicationStatusEnum = ApplicationStatusEnum;

  /* ── Tailwind class strings (shared ones live in assignment-ui.ts) ───── */
  protected readonly ui = ui;
  protected readonly selectClass =
    `cursor-pointer appearance-none rounded-full border border-(--border-subtle) bg-(--mba-surface) py-2 pr-[34px] pl-3.5 text-[13px] font-semibold text-(--fg-1) hover:border-(--border-strong) ${ui.focusRing}`;
  /** Header cells stick to the top of the scrolling card body. */
  protected readonly th =
    'sticky top-0 z-10 h-10 whitespace-nowrap border-b border-(--border-subtle) bg-(--bg-alt) px-3.5 text-left text-[11px] font-bold uppercase tracking-[0.06em] text-(--fg-3)';
  protected readonly td =
    'whitespace-nowrap border-b border-(--border-subtle) px-3.5 py-[11px] align-middle text-(--fg-2) tabular-nums';
  protected readonly cardLine = 'flex flex-wrap items-center justify-between gap-2.5';
  protected readonly emptyIcon =
    'mb-1.5 grid size-12 place-items-center rounded-full bg-(--app-accent-soft) text-(--app-accent)';
  protected readonly emptyTitle = 'font-(family-name:--font-display) text-2xl leading-none font-extrabold text-(--fg-1)';
  protected readonly emptyBody = `max-w-[46ch] ${ui.sub} text-[13px]`;

  protected rowClass(row: AssignmentRow): string {
    const selected = this.selectedIds().has(row.applicant.id);
    return [
      'group cursor-pointer transition-colors duration-[140ms] hover:bg-(--mba-row-hi) focus-visible:bg-(--mba-row-hi) focus-visible:outline-none',
      selected ? 'bg-(--mba-row-hi)' : '',
      // Expired applications fade, but their actions column stays readable.
      row.expired ? '[&>td:not(:last-child)]:opacity-55' : '',
    ].join(' ');
  }

  /** The next-step column sticks to the right edge and matches the row's background. */
  protected actionCellClass(row: AssignmentRow): string {
    const selected = this.selectedIds().has(row.applicant.id);
    return [
      this.td,
      'sticky right-0 z-[1] text-right shadow-[-12px_0_12px_-12px_rgba(18,6,43,0.18)] group-hover:bg-(--mba-row-hi) group-focus-visible:bg-(--mba-row-hi)',
      // Lift the cell whose ⋯ menu is open above the other sticky cells and the header row.
      'has-[[aria-expanded=true]]:z-30',
      selected ? 'bg-(--mba-row-hi)' : 'bg-(--mba-surface)',
    ].join(' ');
  }

  protected cardClass(row: AssignmentRow): string {
    return [
      row.needsYou && !row.expired ? 'shadow-[inset_3px_0_0_var(--app-accent)]' : '',
      this.selectedIds().has(row.applicant.id) ? 'bg-(--mba-row-hi)' : '',
    ].join(' ');
  }

  protected checkboxClass(on: boolean): string {
    return `grid size-[18px] shrink-0 cursor-pointer place-items-center rounded-[5px] border-[1.5px] text-white transition-colors duration-[140ms] ${ui.focusRing} ${
      on ? 'border-(--app-accent) bg-(--app-accent)' : 'border-(--border-strong) bg-(--mba-surface)'
    }`;
  }

  protected verdictClass(verdict: string): string {
    switch (verdict) {
      case 'pass':
        return 'bg-(--success)/10 text-(--success)';
      case 'partial':
        return 'bg-(--warn-dot)/12 text-(--warn)';
      case 'fail':
        return 'bg-(--danger)/10 text-(--danger)';
      default:
        return 'bg-(--app-accent-soft) text-(--app-accent-strong)';
    }
  }

  /** Services */
  public manageBriefTabsService = inject(ManageBriefTabsService);
  public imageService = inject(ImageService);
  public briefService = inject(BriefService);
  public magicSearchService = inject(MagicSearchService);
  private briefApplicationService = inject(BriefApplicationService);
  public pricingModelService = inject(PricingModelV2Service);
  private socialScreeningService = inject(SocialScreeningService);
  private createScreeningService = inject(CreateScreeningService);
  private router = inject(Router);
  public toastNotificationService = inject(ToastNotificationService);
  private alertService = inject(AlertService);
  public centralManagement = inject(CentralManagementService);
  private firebaseService = inject(FirebaseService);
  private authService = inject(AuthService);
  /** Services - END */

  goToLogistics = output<string>();

  private readonly destroyRef = inject(DestroyRef);
  readonly nowMs = signal(Date.now());

  private readonly searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  private subscriptions = new Subscription();
  private searchQuerySubject = new Subject<string>();
  searchQueryModel = signal({ query: '' });
  searchQueryForm = form(this.searchQueryModel);

  showQualifiedOnly = signal(false);

  /* ── UI state ───────────────────────────────────────────────────────── */
  readonly selectedIds = signal<ReadonlySet<string>>(new Set());
  readonly openId = signal<string | null>(null);
  readonly modal = signal<ModalState | null>(null);
  readonly reminding = signal<ReadonlySet<string>>(new Set());
  /** Applications whose creator photo failed to load; their avatar shows initials (F7). */
  readonly brokenPhotos = signal<ReadonlySet<string>>(new Set());

  /* ── Brief context ──────────────────────────────────────────────────── */
  readonly brief = computed(() => this.manageBriefTabsService.focussedBrief());
  /** Applied creators are hired from the side panel, unless the brand must first pick networks. */
  readonly hireInPanel = computed(() => !this.centralManagement.briefAsksToAcceptChannels(this.brief()));
  readonly kind = computed(() => briefKindFor(this.brief()));
  readonly stageLabels = computed(() => assignmentStageLabels(this.kind()));
  readonly brandAutoDeclineDays = computed(() => this.brief()?.businessAccount?.autoDeclineDays);
  readonly hasScreening = computed(() => (this.brief()?.screeningQuestions?.length ?? 0) > 0);
  readonly hasScoredQuestions = computed(() =>
    (this.brief()?.screeningQuestions ?? []).some((q) => q.expected && q.expected.kind !== 'manual'),
  );
  readonly showsShipping = computed(() => this.brief()?.deliveryType == 2);
  readonly briefPrimaryChannelLabel = computed(() => {
    const primary = this.brief()?.shoutBrief?.socialPlatform;
    return primary == null ? '' : getSocialChannelLabel(Number(primary));
  });
  /** "TikTok" rather than "TikTok Post", for copy like "Ready to post on TikTok". */
  readonly channelName = computed(() =>
    this.briefPrimaryChannelLabel().split(' ')[0] || socialChannelName(this.brief()) || 'social',
  );
  readonly briefCurrencyCode = computed(() => (this.brief()?.currentPrice?.currencyCode ?? 'GBP').toUpperCase());
  readonly showCrossPlatformColumns = computed(() => isCrossPlatformBrief(this.brief()?.shoutBrief));
  readonly briefCrossChannels = computed(() => briefCrossChannels(this.brief()?.shoutBrief));

  /* ── Rows ───────────────────────────────────────────────────────────── */
  readonly displayedApplicants = computed(() => {
    const all = this.manageBriefTabsService.filteredApplicants();
    if (!all) return all;
    const visible = excludeHiddenAssignmentApplicants(all);
    if (!this.showQualifiedOnly()) return visible;
    const brief = this.brief();
    return visible.filter((a) => evaluateApplicant(brief, a).verdict === 'pass');
  });

  readonly rows = computed<AssignmentRow[] | undefined>(() => {
    const applicants = this.displayedApplicants();
    if (!applicants) return applicants;
    const now = this.nowMs();
    const brief = this.brief();
    const labels = this.stageLabels();
    const ctx = {
      kind: this.kind(),
      nowMs: now,
      videosRequired: brief?.pieces?.length || brief?.videosPerCreator || null,
      channelLabel: this.channelName(),
    };
    return applicants.map((applicant) => {
      const stage = assignmentStageIndex(applicant.applicationStatus) ?? 0;
      const creator = applicant.creator;
      const stats = listCreatorPlatformStats(creator?.ssCreator)[0];
      return {
        applicant,
        stage,
        stageLabel: labels[stage],
        elapsed: stageElapsed(applicant, now),
        late: isLate(applicant, now),
        needsYou: needsYou(applicant, now),
        expired: isAppliedAutoDeclineExpired(applicant, now),
        step: nextStep(applicant, ctx),
        counts: videoCounts(applicant),
        name: `${creator?.firstName?.trim() ?? ''} ${creator?.lastName?.trim().charAt(0) ?? ''}`.trim() || 'Creator',
        initial: personInitials(creator?.firstName, creator?.lastName),
        photo: creator?.photoURL ? this.imageService.getImageSource(creator.photoURL, 'gallery') : null,
        verdict: this.hasScoredQuestions() && stage === 1 ? evaluateApplicant(brief, applicant) : null,
        // Averages arrive unrounded (983.9215…); at most 2 decimals, as in the side panel.
        avgViews: stats?.averageViews ? Math.round(stats.averageViews * 100) / 100 : null,
        engagement: stats?.averageEngagementRate || null,
      };
    });
  });

  readonly openIndex = computed(() => (this.rows() ?? []).findIndex((row) => row.applicant.id === this.openId()));
  readonly openRow = computed(() => (this.rows() ?? [])[this.openIndex()] ?? null);

  /**
   * Tile counts. Until `assignment-summary` answers (older API), fall back to filter-stats
   * for the per-stage numbers and leave what only the summary knows as unknown.
   */
  readonly tileSummary = computed<TileSummary | null>(() => {
    const summary = this.manageBriefTabsService.assignmentSummary();
    if (summary) return summary;
    const stats = this.manageBriefTabsService.filterStats();
    return {
      needsYou: null,
      invited: stats.Invited ?? 0,
      applied: stats.Applied ?? 0,
      hired: stats.Hired ?? 0,
      started: stats.Started ?? 0,
      delivering: stats.ReadyToPost ?? 0,
      // The page already folds Posted and Paid into Completed.
      completed: stats.Completed ?? 0,
      hiredOrBeyond: (stats.Hired ?? 0) + (stats.Started ?? 0) + (stats.ReadyToPost ?? 0) + (stats.Completed ?? 0),
      invitedWaiting2d: null,
      appliedToReview: null,
      startedWithNewVideos: null,
    };
  });

  /* ── Filters, sort, counts ──────────────────────────────────────────── */
  readonly stageFilter = computed<StageFilter>(() => {
    if (this.manageBriefTabsService.needsYouFilter()) return 'needs';
    const status = this.manageBriefTabsService.selectedFilter();
    const index = STATUS_FOR_STAGE.indexOf(status);
    return index === -1 ? 'all' : (index as StageFilter);
  });

  readonly ratingFilter = computed<RatingFilter>(() => {
    const hasReviews = this.manageBriefTabsService.applicantProfileFilters().hasReviews;
    return hasReviews === true ? 'reviewed' : hasReviews === false ? 'new' : 'all';
  });

  readonly sortOptions = computed(() =>
    assignmentSortOptions((this.manageBriefTabsService.filterStats().Applied ?? 0) > 0),
  );
  readonly selectedSortId = computed(() =>
    this.sortOptions().find((option) => isSameAssignmentSort(option, this.manageBriefTabsService.applicantSort()))?.id ?? '',
  );

  readonly totalOnBrief = computed(() => this.manageBriefTabsService.filterStats().All ?? 0);
  readonly matchingCount = computed(() =>
    this.showQualifiedOnly()
      ? (this.displayedApplicants()?.length ?? 0)
      : (this.manageBriefTabsService.applicantsTotalCount() ?? this.displayedApplicants()?.length ?? 0),
  );

  readonly hasQuery = computed(() =>
    !!this.searchQueryModel().query.trim()
    || this.stageFilter() !== 'all'
    || this.ratingFilter() !== 'all'
    || this.showQualifiedOnly()
    || Object.keys(this.manageBriefTabsService.applicantProfileFilters()).length > 0
    || Object.keys(this.manageBriefTabsService.applicantScreeningFilters()).length > 0,
  );

  /** Which empty state applies when there are no rows. */
  readonly emptyKind = computed<'none' | 'caught-up' | 'no-match'>(() => {
    if (this.stageFilter() === 'needs' && !this.searchQueryModel().query.trim()) return 'caught-up';
    if (this.hasQuery()) return 'no-match';
    return 'none';
  });

  /* ── Selection ──────────────────────────────────────────────────────── */
  readonly selectedRows = computed(() =>
    (this.rows() ?? []).filter((row) => this.selectedIds().has(row.applicant.id)),
  );
  readonly allSelected = computed(() => {
    const rows = this.rows() ?? [];
    return rows.length > 0 && rows.every((row) => this.selectedIds().has(row.applicant.id));
  });
  readonly someSelected = computed(() => this.selectedRows().length > 0 && !this.allSelected());

  /** How many columns the table is showing, for full-width rows. */
  readonly visibleColumnCount = computed(() => {
    let columns = 7; // checkbox, creator, stage, rating, screening, videos, next step
    if (this.showCrossPlatformColumns()) columns += 3;
    if (this.showsShipping()) columns += 1;
    return columns;
  });

  private subscriptionList = new Subscription();

  constructor() {
    // Piece-list Spend cells show the API's quote. Ask for every applicant's once the
    // rows land, and again as tiles are toggled; the store debounces and de-duplicates.
    effect(() => {
      const brief = this.manageBriefTabsService.focussedBrief();
      const rows = this.manageBriefTabsService.filteredApplicants() ?? [];
      this.centralManagement.crossPlatformHireDraft();
      if (!brief?.pieces?.length) {
        return;
      }
      untracked(() => {
        for (const applicant of rows) {
          if (!isApplicant(applicant)) {
            continue;
          }
          this.manageBriefTabsService.pieceQuotes.request(
            applicant.id,
            this.manageBriefTabsService.acceptedPieceChannelIds(applicant),
            this.centralManagement.crossPlatformHireDraft()[applicant.id] ? 250 : 0,
          );
        }
      });
    });

    interval(60000)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.nowMs.set(Date.now()));

    this.subscriptions.add(
      this.searchQuerySubject.pipe(debounceTime(350)).subscribe((query) => {
        // An unchanged term would only re-trigger the list load, 350ms late.
        if ((this.magicSearchService.filtersObjectSignal().searchTerm ?? '') === (query ?? '')) {
          return;
        }
        this.magicSearchService.filtersObjectSignal.set({
          ...this.magicSearchService.filtersObjectSignal(),
          searchTerm: query,
        });
      }),
    );
    this.destroyRef.onDestroy(() => {
      this.subscriptions.unsubscribe();
      this.subscriptionList.unsubscribe();
    });

    // Decline / un-hire are confirmed in the reason modal, which hands off through the
    // same signals the old drawer used; this is still the one place that calls the API.
    effect(() => {
      const declined = this.briefApplicationService.declineApplicationSignal();
      const unhired = this.briefApplicationService.unHireApplicationSignal();
      if (declined) this.declineApplication(declined);
      if (unhired) this.declineApplication(unhired);
    });

    effect(() => {
      this.searchQuerySubject.next(this.searchQueryModel().query);
    });

    // Tile counts follow the brief.
    effect(() => {
      if (this.brief()?.id) {
        this.manageBriefTabsService.loadAssignmentSummary();
      }
    });

    // A hire started elsewhere (the creator profile's Hire button) opens this creator's
    // panel, which holds the hire. Review application briefs keep their own modal.
    effect(() => {
      const applicationId = this.centralManagement.itemisedHireDataSginal()?.briefApplication?.id;
      if (!this.centralManagement.isItemisedHireOpen() || !applicationId || this.centralManagement.hireAsksToAcceptChannels()) {
        return;
      }
      untracked(() => {
        this.openId.set(applicationId);
        this.centralManagement.isItemisedHireOpen.set(false);
      });
    });

    // A creator who filters out of view takes the panel with them.
    effect(() => {
      if (this.openId() && this.rows() && this.openIndex() === -1) {
        this.openId.set(null);
      }
    });
  }

  ngOnInit(): void {
    if (this.selectedSubTab) {
      this.manageBriefTabsService.updateSelectedFilter(this.selectedSubTab as ApplicationStatusEnum);
    }
    // Load the list now, alongside the tile counts; the service skips it if the same load is in flight.
    this.manageBriefTabsService.refreshApplicants();
  }

  /* ── Toolbar ────────────────────────────────────────────────────────── */

  selectStage(filter: StageFilter): void {
    this.selectedIds.set(new Set());
    this.manageBriefTabsService.needsYouFilter.set(filter === 'needs');
    const status = typeof filter === 'number' ? STATUS_FOR_STAGE[filter] : ApplicationStatusEnum.All;
    if (status !== this.manageBriefTabsService.selectedFilter() || filter === 'needs' || filter === 'all') {
      this.manageBriefTabsService.updateSelectedFilter(status);
    }
  }

  onRatingChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value as RatingFilter;
    const { hasReviews, ...rest } = this.manageBriefTabsService.applicantProfileFilters();
    this.manageBriefTabsService.applicantProfileFilters.set(
      value === 'all' ? rest : { ...rest, hasReviews: value === 'reviewed' },
    );
  }

  onSortChange(event: Event): void {
    const id = (event.target as HTMLSelectElement).value;
    const option = this.sortOptions().find((item: FilterItemDto) => item.id === id);
    if (option) {
      this.manageBriefTabsService.applicantSort.set(option);
    }
  }

  resetFilters(): void {
    this.searchQueryModel.set({ query: '' });
    this.showQualifiedOnly.set(false);
    this.manageBriefTabsService.clearApplicantProfileFilters();
    this.selectStage('all');
  }

  onDocumentKeydown(event: KeyboardEvent): void {
    if (event.key !== '/' || this.openId() || this.modal()) return;
    const target = event.target as HTMLElement | null;
    if (target?.closest('input, textarea, select, [contenteditable="true"]')) return;
    event.preventDefault();
    this.searchInput()?.nativeElement.focus();
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.searchQueryModel.set({ query: '' });
    }
  }

  /* ── Selection ──────────────────────────────────────────────────────── */

  toggleRow(id: string): void {
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  toggleAll(): void {
    this.selectedIds.set(
      this.allSelected() ? new Set() : new Set((this.rows() ?? []).map((row) => row.applicant.id)),
    );
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  /* ── Row actions ────────────────────────────────────────────────────── */

  /** Invited and Applied rows open the panel (screening first); hired rows open View Submission. */
  onRowActivate(row: AssignmentRow, event: Event): void {
    if (isRowInteractiveTarget(event.target)) return;
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      event.preventDefault();
    }
    if (row.stage <= 1) {
      this.openId.set(row.applicant.id);
    } else {
      this.openViewSubmission(row.applicant);
    }
  }

  rowTitle(row: AssignmentRow): string {
    return row.stage <= 1 ? `Open ${row.name}’s details` : 'Open View Submission';
  }

  onPrimary(row: AssignmentRow): void {
    switch (row.step.action) {
      case 'remind':
        this.remind([row]);
        break;
      case 'hire':
        // The side panel holds the hire; briefs with networks to pick open Review application.
        if (this.hireInPanel()) {
          this.openId.set(row.applicant.id);
        } else {
          this.manageBriefTabsService.actOnApplicationStatus(row.applicant, ActionTypesEnum.HIRE);
        }
        break;
      case 'view':
        this.openViewSubmission(row.applicant);
        break;
    }
  }

  /** Confirmed in the side panel's hire card. */
  hireFromPanel(row: AssignmentRow): void {
    this.manageBriefTabsService.hireCreator(row.applicant);
  }

  openViewSubmission(applicant: BriefApplicationResponseDto): void {
    this.manageBriefTabsService.actOnApplicationStatus(applicant, ActionTypesEnum.VIEW_MESSAGE);
  }

  openDecline(row: AssignmentRow): void {
    this.modal.set({ kind: 'decline', applicant: row.applicant });
  }

  openUnhire(row: AssignmentRow): void {
    this.modal.set({ kind: 'unhire', applicant: row.applicant });
  }

  withdrawInvite(row: AssignmentRow): void {
    void this.manageBriefTabsService.removeInvitation(row.applicant);
  }

  canUnhire(applicant: BriefApplicationResponseDto): boolean {
    return canUnhireApplication(applicant.applicationStatus, isShoutAgencyAdmin(this.authService.userRoles));
  }

  isBusy(applicant: BriefApplicationResponseDto): boolean {
    return this.manageBriefTabsService.actionsProcessing().includes(applicant.id) || this.reminding().has(applicant.id);
  }

  onReasonConfirmed(reason: string): void {
    const state = this.modal();
    if (!state) return;
    const payload: DeclineBriefApplicationDto = {
      application: state.applicant,
      status: state.kind === 'decline' ? ApplicationStatusEnum.ApplicationDeclined : ApplicationStatusEnum.HiredRemoved,
      user: this.firebaseService.userId || '',
      reason,
    };
    if (state.kind === 'decline') {
      this.briefApplicationService.declineApplicationSignal.set(payload);
    } else {
      this.briefApplicationService.unHireApplicationSignal.set(payload);
    }
    this.modal.set(null);
  }

  nameOf(applicant: BriefApplicationResponseDto): string {
    const creator = applicant.creator;
    return `${creator?.firstName?.trim() ?? ''} ${creator?.lastName?.trim().charAt(0) ?? ''}`.trim() || 'Creator';
  }

  hasUploads(applicant: BriefApplicationResponseDto): boolean {
    const counts = videoCounts(applicant);
    return counts.new + counts.seen + counts.done > 0;
  }

  /* ── Panel ──────────────────────────────────────────────────────────── */

  openDetails(row: AssignmentRow): void {
    this.openId.set(row.applicant.id);
  }

  movePanel(delta: number): void {
    const rows = this.rows() ?? [];
    const next = rows[this.openIndex() + delta];
    if (next) {
      this.openId.set(next.applicant.id);
    }
  }

  autoDeclineOn(applicant: BriefApplicationResponseDto): boolean {
    return isAutoDeclineEnabled(this.brandAutoDeclineDays(), applicant.autoDeclineAt) && !!applicant.autoDeclineAt;
  }

  /** What the brand pays for this creator, as shown in the panel's payment block. */
  costText(applicant: BriefApplicationResponseDto): string {
    if (this.manageBriefTabsService.isShoutPlusBrief() && !this.manageBriefTabsService.getTotalPriceShoutPlus()) {
      return '';
    }
    const amount = this.spendHeadline(applicant);
    if (!amount) return '';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: this.briefCurrencyCode() }).format(amount);
  }

  costNote(row: AssignmentRow): string {
    if (row.stage === 5) return 'Paid';
    return this.kind() === 'influence' ? 'Paid when you confirm the post' : 'Paid when you approve the delivery';
  }

  /* ── Remind / bulk ──────────────────────────────────────────────────── */

  remind(rows: AssignmentRow[]): void {
    const invited = rows.filter((row) => row.stage === 0);
    if (invited.length === 0) {
      this.toastNotificationService.alert({
        title: 'Nobody to remind',
        message: 'Reminders go to invited creators who haven’t applied yet.',
        type: 'info',
        icon: 'info',
      });
      return;
    }
    const ids = invited.map((row) => row.applicant.id);
    this.reminding.update((current) => new Set([...current, ...ids]));
    this.briefApplicationService.remindCreators(ids).pipe(take(1)).subscribe({
      next: (result) => {
        this.reminding.update((current) => new Set([...current].filter((id) => !ids.includes(id))));
        const sent = result.sent.length;
        const rateLimited = result.skipped.filter((skip) => skip.reason === 'rate-limited').length;
        const skippedOther = rows.length - invited.length;
        const title = sent === 1 && invited.length === 1
          ? `Reminder sent to ${invited[0].name}`
          : sent > 0 ? `Reminder sent to ${sent} ${sent === 1 ? 'creator' : 'creators'}` : 'No reminders sent';
        const notes = [
          rateLimited ? `${rateLimited} already reminded in the last 24 hours` : '',
          skippedOther ? `${skippedOther} not invited` : '',
        ].filter(Boolean).join(' · ');
        this.toastNotificationService.alert({
          title,
          message: notes || 'They’ll get a push notification.',
          type: sent > 0 ? 'success' : 'warning',
          icon: sent > 0 ? 'success' : 'warning',
        });
      },
      error: () => {
        this.reminding.update((current) => new Set([...current].filter((id) => !ids.includes(id))));
        this.toastNotificationService.alert({
          title: 'Reminder failed',
          message: 'We couldn’t send the reminder. Try again in a moment.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  remindSelected(): void {
    this.remind(this.selectedRows());
  }

  promoteSelected(): void {
    const eligible = this.selectedRows()
      .map((row) => row.applicant)
      .filter((applicant) => applicant.hiredBriefPricing || PROMOTABLE_STATUSES.has(applicant.applicationStatus));
    if (eligible.length === 0) {
      this.toastNotificationService.alert({
        title: 'Nobody to promote',
        message: 'Only hired creators can be promoted to another brief.',
        type: 'info',
        icon: 'info',
      });
      return;
    }
    this.briefService.creatorsToPromote.set(eligible);
    this.briefService.showPromoteToBriefDrawer.set(true);
  }

  screenSelected(): void {
    const applicants = this.selectedRows()
      .map((row) => row.applicant)
      .filter((applicant) =>
        applicant.applicationStatus === ApplicationStatusEnum.Invited || applicant.applicationStatus === ApplicationStatusEnum.Applied,
      );
    if (applicants.length === 0) {
      this.toastNotificationService.alert({
        title: 'Nobody to screen',
        message: 'Screening is for invited and applied creators.',
        type: 'info',
        icon: 'info',
      });
      return;
    }
    this.briefService.creatorsToScreen.set(applicants);
    this.openScreenCreators();
  }

  openScreenCreators(): void {
    const creators = this.briefService.creatorsToScreen()
      .map((application) => application.creator)
      .filter((creator): creator is NonNullable<typeof creator> => !!creator);
    const screenable = creators.filter((creator) => this.socialScreeningService.canScreenCreator(creator));
    const skipped = creators.length - screenable.length;

    if (screenable.length === 0) {
      void this.alertService.alert({
        title: "Can't screen these creators",
        message: skipped === 1
          ? this.socialScreeningService.cannotScreenMessage(creators[0])
          : "None of these creators have an Instagram or TikTok handle, so they can't be added to a screening list.",
        type: 'alert',
        icon: 'warning',
        confirmText: 'OK',
        cancelText: 'Close',
      });
      return;
    }

    if (skipped > 0) {
      void this.toastNotificationService.alert({
        title: skipped === 1 ? '1 creator was skipped' : `${skipped} creators were skipped`,
        message: "They haven't got an Instagram or TikTok handle, so they can't be screened.",
        type: 'warning',
        icon: 'warning',
      });
    }

    this.socialScreeningService.selectedCreators.set(screenable);
    this.createScreeningService.usePreselectedCreators.set(true);
    this.router.navigate(['/screening-brief-select']);
  }

  /* ── Empty state ────────────────────────────────────────────────────── */

  getInviteCreatorsLink(): string {
    const brief = this.brief();
    return brief ? `/creators-v4-filter/${brief.id}` : `/creators-v4-filter`;
  }

  turnOnAutoPilot(): void {
    const brief = this.brief();
    if (!brief || brief.enableAutoPilot) return;
    this.briefService.toggleAutoPilot(brief.id).pipe(take(1)).subscribe({
      next: () => {
        this.manageBriefTabsService.focussedBrief.set({ ...brief, enableAutoPilot: true });
        this.toastNotificationService.alert({
          title: 'Auto Pilot on',
          message: 'SHOUT will invite and shortlist matching creators',
          type: 'success',
          icon: 'success',
        });
      },
      error: () => {
        this.toastNotificationService.alert({
          title: 'Auto Pilot didn’t turn on',
          message: 'Try again in a moment.',
          type: 'error',
          icon: 'error',
        });
      },
    });
  }

  /* ── Infinite scroll ────────────────────────────────────────────────── */

  onScrollDown(): void {
    if (this.manageBriefTabsService.isLoadingMore() || !this.manageBriefTabsService.hasMoreApplicants()) return;
    this.manageBriefTabsService.pageNumber++;
    const filters = this.manageBriefTabsService.buildApplicantFilters();
    this.manageBriefTabsService.creatorFilters = filters;
    this.manageBriefTabsService.getBriefApplicants(
      this.manageBriefTabsService.focussedBriefId,
      filters,
      this.manageBriefTabsService.pageNumber,
    );
  }

  /* ── Existing behaviour kept from the previous table ───────────────── */

  isApplicantExpired(applicant: BriefApplicationResponseDto): boolean {
    return isAppliedAutoDeclineExpired(applicant, this.nowMs());
  }

  canOpenApplicantProfile(applicant: BriefApplicationResponseDto): boolean {
    return !this.isApplicantExpired(applicant) && !!applicant.creator;
  }

  /** Opens the creator's SHOUT profile (the full-screen creator profile, with prev/next). */
  toggleCreatorPortfolio(applicant: BriefApplicationResponseDto) {
    if (this.isApplicantExpired(applicant) || !applicant.creator) {
      return;
    }
    const navItems = (this.manageBriefTabsService.filteredApplicants() ?? [])
      .filter((item) => !!item.creator)
      .map((item) => ({ creator: item.creator!, application: item }));
    this.magicSearchService.openCreatorPortfolio(applicant.creator, {
      application: applicant,
      navItems,
    });
  }

  navigateToLogistics(applicant: BriefApplicationResponseDto): void {
    this.manageBriefTabsService.logisticsHighlightApplicationId.set(applicant.id);
    this.goToLogistics.emit(applicant.id);
  }

  showsShippingAction(applicant: BriefApplicationResponseDto): boolean {
    const stage = assignmentStageIndex(applicant.applicationStatus);
    return stage != null && stage >= 2;
  }

  copyTtoInviteLink(link: string): void {
    navigator.clipboard.writeText(link).then(() => {
      this.toastNotificationService.alert({
        title: 'Copied!',
        message: 'TikTok One invite link copied',
        type: 'success',
        icon: 'success',
      });
    }).catch(() => {
      this.toastNotificationService.alert({
        title: 'Copy failed',
        message: 'Could not copy the invite link',
        type: 'error',
        icon: 'error',
      });
    });
  }

  declineApplication(declineObject: DeclineBriefApplicationDto) {
    const subDecline = this.briefApplicationService.adminSetApplicationStatus(declineObject.application.id, declineObject).subscribe({
      next: response => {
        if (response) {
          const updatedApplication = response as BriefApplicationResponseDto;
          const newStatus = updatedApplication.applicationStatus ?? declineObject.status;
          const applicants = this.manageBriefTabsService.filteredApplicants();

          if (applicants?.length) {
            const foundIndex = applicants.findIndex(appy => appy.id === updatedApplication.id);
            if (foundIndex !== -1) {
              if (
                newStatus === ApplicationStatusEnum.HiredRemoved ||
                newStatus === ApplicationStatusEnum.ApplicationDeclined
              ) {
                this.manageBriefTabsService.filteredApplicants.set(
                  applicants.filter((appy) => appy.id !== updatedApplication.id),
                );
                const total = this.manageBriefTabsService.applicantsTotalCount();
                if (typeof total === 'number' && total > 0) {
                  this.manageBriefTabsService.applicantsTotalCount.set(total - 1);
                }
              } else {
                const updatedApplicants = [...applicants];
                updatedApplicants[foundIndex] = {
                  ...updatedApplicants[foundIndex],
                  applicationStatus: newStatus,
                  applicationStatusModifiedDate:
                    updatedApplication.applicationStatusModifiedDate ?? updatedApplicants[foundIndex].applicationStatusModifiedDate,
                };
                this.manageBriefTabsService.filteredApplicants.set(updatedApplicants);
              }
            }
          }

          this.manageBriefTabsService.loadAssignmentSummary();
          const firstName = declineObject.application?.creator?.firstName ?? 'Creator';
          this.toastNotificationService.alert({
            title: declineObject.status === ApplicationStatusEnum.ApplicationDeclined ? `${firstName} declined` : `${firstName} un-hired`,
            message: declineObject.status === ApplicationStatusEnum.ApplicationDeclined
              ? 'The application has been declined'
              : 'The creator has been un-hired',
            type: 'success',
            icon: 'success'
          });
        }
      },
      error: err => {
        this.toastNotificationService.alert({
          title: `${declineObject.application?.creator?.firstName}\'s ${declineObject.status === ApplicationStatusEnum.ApplicationDeclined ? 'decline' : 'un-hire'} failed`,
          message: 'Something went wrong. Try again in a moment.',
          type: 'error',
          icon: 'error'
        });
        console.error('Error - application decline', err)
      }
    });
    this.subscriptionList.add(subDecline);
    this.briefApplicationService.declineApplicationSignal.set(null);
    this.briefApplicationService.unHireApplicationSignal.set(null);
  }

  /* ── Cross-platform columns (cross-post briefs only) ───────────────── */

  draftIncluded(applicant: BriefApplicationResponseDto): number[] {
    return this.centralManagement.crossPlatformDraftFor(
      applicant.id,
      togglableChannels(applicant, this.brief()?.shoutBrief),
    );
  }

  crossPlatformCells(applicant: BriefApplicationResponseDto): CrossPlatformCell[] {
    return crossPlatformCells(
      applicant,
      this.brief()?.shoutBrief,
      { draftIncluded: isApplicant(applicant) ? this.draftIncluded(applicant) : undefined },
    );
  }

  isChannelTogglable(applicant: BriefApplicationResponseDto, socialChannel: number): boolean {
    if (this.isApplicantExpired(applicant)) {
      return false;
    }
    return togglableChannels(applicant, this.brief()?.shoutBrief).includes(socialChannel);
  }

  onToggleCrossPlatform(applicant: BriefApplicationResponseDto, socialChannel: number): void {
    this.centralManagement.toggleCrossPlatformDraft(
      applicant.id,
      socialChannel,
      togglableChannels(applicant, this.brief()?.shoutBrief),
    );
  }

  crossPlatformSummary(applicant: BriefApplicationResponseDto): string | null {
    return putForwardSummary(applicant, this.brief()?.shoutBrief, this.draftIncluded(applicant));
  }

  primaryPostState(applicant: BriefApplicationResponseDto): PrimaryPostState {
    return primaryPostState(applicant, this.brief()?.shoutBrief);
  }

  primaryTileState(applicant: BriefApplicationResponseDto): 'posted' | 'assigned' | 'locked' | 'none' {
    switch (this.primaryPostState(applicant)) {
      case 'posted':
        return 'posted';
      case 'assigned':
        return 'assigned';
      case 'waiting':
        return 'locked';
      default:
        return 'none';
    }
  }

  primaryPostStatus(applicant: BriefApplicationResponseDto): SubmittedPostStatusEnum | null {
    const primary = this.brief()?.shoutBrief?.socialPlatform;
    return primary == null ? null : postStatusFor(applicant, Number(primary));
  }

  crossPlatformPrice(applicant: BriefApplicationResponseDto, socialChannel: number): number {
    const brief = this.brief();
    if (!brief || !applicant.creator) {
      return 0;
    }
    return this.pricingModelService.getAudiencePrice(socialChannel, brief, applicant.creator);
  }

  /** A photo URL that will not load falls back to initials instead of a broken image. */
  markPhotoBroken(applicationId: string): void {
    this.brokenPhotos.update((ids) => (ids.has(applicationId) ? ids : new Set(ids).add(applicationId)));
  }

  /**
   * What the brand pays for this creator: the VAT-inclusive primary fee plus every included
   * cross-post. For an applicant it moves as tiles are toggled; once hired it is committed.
   *
   * Piece-list briefs use the API quote, or the total frozen at hire. Null while that
   * quote is still on its way — Studio does not price pieces itself.
   */
  spendHeadline(applicant: BriefApplicationResponseDto): number | null {
    if (this.brief()?.pieces?.length) {
      return this.manageBriefTabsService.pieceHireTotal(applicant);
    }
    const primaryGross = this.manageBriefTabsService.getTotalPriceV2(applicant) ?? 0;
    return primaryGross + this.crossPlatformSpendDelta(applicant);
  }

  crossPlatformSpendDelta(applicant: BriefApplicationResponseDto): number {
    if (this.brief()?.pieces?.length) {
      return 0;
    }
    const channels = isApplicant(applicant)
      ? this.draftIncluded(applicant)
      : readChannels(applicant.crossPlatformsAccepted);
    const net = crossPostNetTotal(channels.map((channel) => this.crossPlatformPrice(applicant, channel)));
    return applyVat(net, this.brief(), this.manageBriefTabsService.paymentMethod());
  }

  isApplicantRow(applicant: BriefApplicationResponseDto): boolean {
    return isApplicant(applicant);
  }
}
